<template>
	<div class="order-history">
		<div v-if="orders.length && !error" class="order-history__info">
			<div class="order-history__info-item order-history__info-item_1">
				{{ $t(`Date`) }}
			</div>
			<div class="order-history__info-item order-history__info-item_2">
				{{ $t(`Item`) }}
			</div>
			<div class="order-history__info-item order-history__info-item_3">
				{{ $t(`Quantity`) }}
			</div>
			<div class="order-history__info-item order-history__info-item_4">
				{{ $t(`Total`) }}
			</div>
			<div class="order-history__info-item order-history__info-item_5">
				{{ $t(`Status`) }}
			</div>
		</div>
		<div v-if="!orders.length && !error" class="order-history__empty _l">
			{{ $t("You don't have any orders yet.") }}
		</div>
		<div v-if="orders.length && !error" class="order-history__body">
			<div
				class="order-history__item"
				v-for="order in orders"
				:key="order.id"
			>
				<p
					class="order-history__date"
					:data-da="`.order-history__middle_${order.id},767.98,first`"
				>
					{{ formatDate(order.created_at) }}
				</p>
				<div class="order-history__about">
					<div class="order-history__image-container _ibg-contain">
						<div class="order-history__image _ibg-contain">
							<img :src="imgDomain + order.img_url" :alt="order.title" />
						</div>
					</div>
					<p class="order-history__title">
						{{ getSkinName(order?.title) }}
					</p>
				</div>
				<div class="order-history__count">{{ order.count }}</div>
				<div
					class="order-history__price"
					:data-da="`.order-history__middle_${order.id},767.98,last`"
				>
					<div class="order-history__real">
						<span>{{ order.sum }}</span>
						<span>{{ order?.currency }}</span>
					</div>
				</div>
				<span
					class="order-history__status-badge"
					:class="`order-history__status-badge_${getStatusClass(
						order.status
					)}`"
				>
					{{ getStatus(order.status) }}
				</span>
			</div>
		</div>
		<div v-if="error" class="order-history__error-message _l">
			{{ $t('Failed to fetch orders.') }}
		</div>
	</div>
</template>

<script>
import { initDynamicAdapt } from '@/utils/dynamic_adapt.js';
import moment from 'moment';

const STATUS_CLASS_MAP = {
	processing: 'pending',

	accepted: 'success',
	completed: 'success',
	sold: 'success',

	denied: 'error',
	deleted: 'error',
	'client refused': 'error',
	'payment error': 'error',

	unknown: 'error',
};

export default {
	name: 'orderHistory',
	data() {
		return {
			imgDomain: '',
			orders: [],
			error: false,
		};
	},
	methods: {
		getSkinName(title) {
			if (!title) return '';
			const match = title.match(/\|\s*([^()]+?)(?:\(|$)/);
			return match ? match[1].trim() : title;
		},
		formatDate(timestamp) {
			return moment.unix(timestamp).format('DD.MM.YYYY HH:mm');
		},
		formatCurrency(amount, currency) {
			return `${currency} ${parseFloat(amount).toFixed(2)}`;
		},
		getStatus(status) {
			switch (status.toLowerCase()) {
				case 'deleted':
					return this.$t('Deleted');
				case 'denied':
					return this.$t('Denied');
				case 'processing':
					return this.$t('Processing');
				case 'accepted':
					return this.$t('Accepted');
				case 'completed':
					return this.$t('Completed');
				case 'client refused':
					return this.$t('Client refused');
				case 'payment error':
					return this.$t('Payment error');
				case 'sold':
					return this.$t('Sold');
				default:
					return this.$t('Unknown');
			}
		},
		getStatusClass(status) {
			if (!status) return 'error';

			const key = status.toLowerCase();
			return STATUS_CLASS_MAP[key] || 'error';
		},
		fetchorderHistory() {
			this.$http
				.get(`${process.env.VUE_APP_API}purchase/history`)
				.then((res) => {
					this.orders = res.data.payload;
					this.error = false;
				})
				.catch((error) => {
					this.error = true;
					if (error.response && error.response.status === 401) {
						this.$emit('open-sign-in');
					}
				});
		},
	},
	created() {
		this.fetchorderHistory();
	},
	mounted() {
		this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN;
		initDynamicAdapt('max');
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.order-history {
	overflow-x: auto;
	&__info {
		min-width: 860px;
		display: flex;
		align-items: center;
		padding-top: 10px;
		padding-bottom: 10px;
		@include adaptiveValue('padding-left', 70, 10, 1470, 992, 1);
		@include adaptiveValue('padding-right', 70, 10, 1470, 992, 1);
		border-radius: var(--border-radius-primary);
		background-color: rgba(196, 196, 196, 0.3);
		gap: 20px;
		@media (max-width: $md2) {
			padding: 10px;
		}
		@media (min-width: $md3) {
			min-height: 60px;
		}
	}
	&__info-item {
		display: flex;
		font-weight: 700;
		line-height: 150%;
		font-family: var(--font-family-inter);
		font-size: 16px;
		color: var(--primary-color);
		text-transform: uppercase;
		flex: 0 1 20%;
		&_1 {
		}
		&_2 {
			flex: 0 1 30%;
			display: flex;
			justify-content: center;
		}
		&_3 {
			flex: 0 1 15%;
			display: flex;
			justify-content: center;
		}
		&_4 {
			flex: 0 1 15%;
			display: flex;
			justify-content: center;
		}
		&_5 {
		}
	}
	&__empty {
		text-align: center;
		margin-top: 15px;
	}
	&__body {
		min-width: 860px;
	}
	&__item {
		width: 100%;
		border-bottom: 1px solid rgba(196, 196, 196, 0.3);
		display: flex;
		align-items: center;
		gap: 20px;
		padding-top: 10px;
		padding-bottom: 10px;
		@include adaptiveValue('padding-left', 70, 10, 1470, 992, 1);
		@include adaptiveValue('padding-right', 70, 10, 1470, 992, 1);
		@media (max-width: $md2) {
			padding: 10px;
		}
		@media (min-width: $md3) {
			min-height: 64px;
		}
	}
	&__date {
		font-weight: 500;
		white-space: nowrap;
		flex: 0 1 20%;
	}
	&__about {
		flex: 0 1 30%;
		display: flex;
		align-items: center;
		gap: 15px;
	}
	&__image-container {
		flex: 0 0 80px;
		min-width: 80px;
		height: 80px;
	}
	&__image {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		width: 80px;
		height: 60px;
	}
	&__title {
		font-weight: 400;
		max-width: 230px;
	}
	&__count {
		flex: 0 1 15%;
		display: flex;
		justify-content: center;
	}

	&__price {
		flex: 0 1 15%;
		display: flex;
		justify-content: center;
		gap: 5px;
	}
	&__real {
		gap: 5px;
		display: flex;
		font-weight: 400;
	}
	&__status-badge {
		flex: 0 1 20%;
		font-weight: 400;
		text-transform: capitalize;
		&_pending {
			color: var(--pending-color);
		}
		&_success {
			color: var(--success-color);
		}
		&_error {
			color: var(--error-color);
		}
	}
	&__error-message {
		margin-top: 15px;
		text-align: center;
		color: var(--error-color);
	}
}
</style>
